import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAInstallPrompt: React.FC = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone =
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
      (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    if (standalone) return; // Already installed, don't show

    // Check if permanently dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed === 'permanent') return;

    // Only show on Mondays (or if never shown before)
    const lastShown = localStorage.getItem('pwa-prompt-last-shown');
    if (lastShown) {
      const lastDate = new Date(parseInt(lastShown));
      const now = new Date();
      // Check if today is Monday (1) and we haven't shown it yet this Monday
      const isMonday = now.getDay() === 1;
      const isNewMonday = lastDate.toDateString() !== now.toDateString();
      if (!isMonday || !isNewMonday) return;
    }

    // Detect iOS
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    let hasInteracted = false;
    const markInteracted = () => {
      hasInteracted = true;
    };
    const interactionEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    interactionEvents.forEach((evt) => window.addEventListener(evt, markInteracted, { once: true }));

    const cleanupInteraction = () => {
      interactionEvents.forEach((evt) => window.removeEventListener(evt, markInteracted));
    };

    // On iOS, show our custom prompt after a 5s delay if user hasn't interacted
    if (isIOSDevice) {
      const timer = setTimeout(() => {
        if (!hasInteracted) setShowPrompt(true);
      }, 5000);
      return () => {
        clearTimeout(timer);
        cleanupInteraction();
      };
    }

    // On Android/Chrome, listen for the native install event
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      pendingTimer = setTimeout(() => {
        if (!hasInteracted) setShowPrompt(true);
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (pendingTimer) clearTimeout(pendingTimer);
      cleanupInteraction();
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      // PWA install accepted
    }
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    // "Got it" — dismiss for now, will show again next Monday
    localStorage.setItem('pwa-prompt-last-shown', Date.now().toString());
    setShowPrompt(false);
  };

  const handleNeverShow = () => {
    localStorage.setItem('pwa-prompt-dismissed', 'permanent');
    setShowPrompt(false);
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-slide-up">
        {/* Header with brand */}
        <div className="bg-gradient-to-r from-[#0A1937] to-[#0f2a5e] px-6 pt-6 pb-5 text-center">
          <div className="w-16 h-16 bg-white/10 backdrop-blur rounded-2xl mx-auto flex items-center justify-center mb-3 border border-white/20">
            <span className="text-3xl font-bold text-white">VC</span>
          </div>
          <h2 className="text-white text-lg font-bold">{t('pwa_prompt.title')}</h2>
          <p className="text-blue-200 text-sm mt-1">{t('pwa_prompt.subtitle')}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {isIOS ? (
            /* iOS instructions */
            <div className="space-y-4">
              <p className="text-gray-700 text-sm text-center">
                {t('pwa_prompt.ios_intro')}
              </p>
              <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
                <div className="w-7 h-7 rounded-full bg-[#0085FF] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                <div>
                  <p className="text-sm text-gray-800 font-medium">
                    {t('pwa_prompt.ios_step1')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
                <div className="w-7 h-7 rounded-full bg-[#0085FF] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                <div>
                  <p className="text-sm text-gray-800 font-medium">
                    {t('pwa_prompt.ios_step2')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Android/Chrome — native install */
            <div className="space-y-3 text-center">
              <p className="text-gray-600 text-sm">
                {t('pwa_prompt.android_body')}
              </p>
              <div className="flex gap-6 justify-center py-2">
                <div className="text-center">
                  <div className="text-2xl mb-1">⚡</div>
                  <p className="text-xs text-gray-500">{t('pwa_prompt.feature_instant')}</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">🔔</div>
                  <p className="text-xs text-gray-500">{t('pwa_prompt.feature_notifications')}</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">📱</div>
                  <p className="text-xs text-gray-500">{t('pwa_prompt.feature_fullscreen')}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="px-6 pb-6 space-y-2">
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="w-full bg-[#0085FF] hover:bg-[#0070d6] text-white font-bold py-3.5 rounded-xl transition-all active:scale-[0.98] text-sm"
            >
              {t('pwa_prompt.install')}
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 text-gray-400 hover:text-gray-600 font-medium py-2 rounded-xl border border-gray-100 transition-colors text-sm"
            >
              {isIOS ? t('pwa_prompt.got_it') : t('pwa_prompt.maybe_later')}
            </button>
            <button
              onClick={handleNeverShow}
              className="flex-1 text-gray-300 hover:text-gray-500 font-medium py-2 rounded-xl transition-colors text-xs"
            >
              {t('pwa_prompt.never_show')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
